import React, { useState } from 'react';

import Grid from '@mui/material/Grid2';

import { 
  AttachedTable,
  ResourceFeature,
  UpgradableNumber
} from "@dndModels";
import { ABILITY_SCORES, LEVELED_SPELL_LEVELS_STRINGS, REFRESH_RULES } from "@dndModels/Constants";
import { AbilityScoreType, SpellLevel } from "@dndModels/Types";

import UpgradableNumberBox from "@dndComponents/UpgradableNumberBox";
import ToggleButtonBox from "@components/input/buttons/ToggleButtonBox";

import SelectResourceBox from "@dndComponents/select/SelectResourceBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import StringBox from "@components/input/strings/StringBox";
import CheckBox from "@components/input/CheckBox";
import SelectObjectBox from "@components/input/selects/SelectObjectBoxSimple";

interface Props {
  obj: ResourceFeature;
  onChange: (changed: ResourceFeature) => void; 
  disabled: boolean;
  attached_table?: AttachedTable;
}

export default function ResourceFeatureInput({
  obj,
  onChange,
  disabled,
  attached_table
}: Props) {
  const [ping, setPing] = useState(false);

  return (
    <Grid container spacing={0} direction="row">
      <Grid
        size={{
          xs: 6
        }}>
        <CheckBox 
          name="Slots" 
          value={obj.slots} 
          disabled={disabled}
          onChange={(changed: boolean) => {
            const new_obj = obj;
            new_obj.slots = changed;
            onChange(new_obj);
            setPing(!ping);
          }} 
        />
      </Grid>
      { obj.slots ?
        <>
          <Grid
            size={{
              xs: 3
            }}>
            <SelectObjectBox 
              name="Slot Type" 
              data_type="spell_slot_type"
              display_format="{obj.true_name}"
              value={obj.type_id} 
              disabled={disabled}
              onChange={(changed: string) => {
                const new_obj = obj;
                new_obj.type_id = changed;
                onChange(new_obj);
                setPing(!ping);
              }} 
            />
          </Grid>
          <Grid
            size={{
              xs: 3
            }}>
            <SelectStringBox 
              name="Slot Level" 
              options={LEVELED_SPELL_LEVELS_STRINGS}
              extra_options={["Use Size"]}
              value={ obj.slot_level === -1 ? "Use Size" : `${obj.slot_level}`} 
              disabled={disabled}
              onChange={(changed: string) => {
                const new_obj = obj;

                new_obj.slot_level = changed === "Use Size" ? -1 : (+changed) as SpellLevel;
                onChange(new_obj);
                setPing(!ping);
              }} 
            />
          </Grid>
          <Grid
            size={{
              xs: 12
            }}>
            <StringBox
              name="Slot Name"
              value={obj.slot_name}
              disabled={disabled}
              onBlur={(changed: string) => {
                const new_obj = obj;
                new_obj.slot_name = changed;
                onChange(new_obj);
                setPing(!ping);
              }}
            />
          </Grid>
        </>
      :
        <Grid
          size={{
            xs: 6
          }}>
          <SelectResourceBox 
            name="Resource Type" 
            value={obj.type_id ?? ""} 
            disabled={disabled}
            onChange={(changed: string) => {
              const new_obj = obj;
              new_obj.type_id = changed;
              onChange(new_obj);
              setPing(!ping);
            }} 
          />
        </Grid>
      }
      <Grid
        size={{
          xs: 6
        }}>
        <StringBox
          name="Override Name"
          value={obj.override_name}
          onBlur={(changed: string) => {
            const new_obj = obj;
            new_obj.override_name = changed;
            onChange(new_obj);
            setPing(!ping);
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 6
        }}>
        <StringBox
          name="Override Singular"
          value={obj.override_singular}
          onBlur={(changed: string) => {
            const new_obj = obj;
            new_obj.override_singular = changed;
            onChange(new_obj);
            setPing(!ping);
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 12
        }}>
        <UpgradableNumberBox 
          name="Total"
          value={obj.total} 
          disabled={disabled}
          attached_table={attached_table}
          quick_options={[...(ABILITY_SCORES as AbilityScoreType[]),"Prof"]}
          onChange={(value: UpgradableNumber) => {
            const new_obj = obj;
            new_obj.total = value;
            onChange(new_obj);
            setPing(!ping);
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 12
        }}>
        <UpgradableNumberBox 
          name="Size"
          value={obj.size} 
          disabled={disabled}
          // log_it
          attached_table={attached_table}
          onChange={(value: UpgradableNumber) => {
            const new_obj = obj;
            new_obj.size = value;
            onChange(new_obj);
            setPing(!ping);
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 12
        }}>
        <SelectStringBox 
          options={[...REFRESH_RULES,"Inherit"]} 
          value={obj.refresh_rule}
          name="Refresh Rule"
          onChange={(changed: string) => {
            const new_obj = obj;
            new_obj.refresh_rule = changed;
            onChange(new_obj);
            setPing(!ping);
          }}
        />
      </Grid>
      { obj.refresh_rule === "Long Rest" &&
        <Grid
          size={{
            xs: 12
          }}>
          <UpgradableNumberBox  
            name="Refresh on Short Rest"
            value={obj.refresh_x_on_short}
            attached_table={attached_table}
            onChange={(value: UpgradableNumber) => {
              const new_obj = obj;
              new_obj.refresh_x_on_short = value.clone();
              onChange(new_obj);
              setPing(!ping);
            }}
          />
        </Grid>
      }
      <Grid
        size={{
          xs: 12
        }}>
        <ToggleButtonBox 
          name="Stacks"
          value={obj.stacks} 
          disabled={disabled}
          onToggle={() => {
            const new_obj = obj;
            new_obj.stacks = !new_obj.stacks;
            onChange(new_obj);
            setPing(!ping);
          }}
        />
      </Grid>
    </Grid>
  );
}
